import React, { useEffect, useState, useContext, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Modal,
  Button,
  Form,
  Table,
  Offcanvas,
  Collapse,
} from "react-bootstrap";
import { getAvailableOffers } from "../../../../redux/slices/offers";
import {
  activateSims,
  validateSimsForAction,
} from "../../../../redux/slices/sims";
import { FaCheckCircle, FaExclamationCircle } from "react-icons/fa";
import NotyfContext from "../../../../contexts/NotyfContext";
import { Formik } from "formik";
import * as Yup from "yup";
import Loader from "../../../../components/spinners/Loader";
import useWindowSize from "../../../../hooks/useWindowSize";
import useDeviceType from "../../../../hooks/useDeviceType";

const ActivateSimModal = ({
  openActivateModal,
  setOpenActivateModal,
  selectedSims,
  clearSelectedRows,
}) => {
  const whitelabel = useSelector((state) => state.whitelabel.whitelabel);
  const dispatch = useDispatch();
  // eslint-disable-next-line no-unused-vars
  const { user } = useSelector((state) => state.user);
  const { impersonationUser } = useSelector((state) => state.impersonationUser);
  const [activeUser, setActiveUser] = useState({});
  const availableOffers = useSelector((state) => state.offers.availableOffers);
  const notyf = useContext(NotyfContext);
  const [currentStep, setCurrentStep] = useState(1);
  const [width] = useWindowSize();
  const deviceType = useDeviceType();
  const isDesktop = deviceType === "desktop";
  const [internalShow, setInternalShow] = useState(openActivateModal);
  const [fadeOut, setFadeOut] = useState(false);
  const [fileInputKey, setFileInputKey] = React.useState(Date.now());
  const customFields = useSelector((state) => state.customFields.customFields);
  const handleToggleModal = useCallback(() => {
    setOpenActivateModal(false);
  }, [setOpenActivateModal]);

  useEffect(() => {
    if (openActivateModal && !isDesktop) {
      setFadeOut(false);
      setInternalShow(true);
    }
  }, [openActivateModal, isDesktop]);

  const handleHide = () => {
    setFadeOut(true);
    setTimeout(() => {
      setInternalShow(false);
      handleToggleModal();
    }, 300);
  };

  const initialValues = {
    sims: selectedSims.length > 0 ? selectedSims : [],
    offer: "",
    file: null,
    addCustomFieldValues: false,
    customFieldValues: (customFields || []).map((cf) => ({
      customFieldId: cf.id,
      displayName: cf.displayName,
      value: "",
    })),
  };

  const validation = Yup.object().shape({
    sims: Yup.array().required("Required"),
    offer: Yup.string().required("Required"),
    file: Yup.mixed().when("$requireFile", {
      is: true,
      then: Yup.mixed()
        .required("File is required")
        .test("fileType", "Only CSV or XLSX files are accepted", (value) => {
          if (!value) return false;
          const allowedTypes = [
            "text/csv",
            "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            "application/vnd.ms-excel",
          ];
          return allowedTypes.includes(value.type);
        }),
      otherwise: Yup.mixed().nullable().notRequired(),
    }),
  });

  useEffect(() => {
    setActiveUser(impersonationUser.id ? impersonationUser : user);
  }, [user, impersonationUser]);

  useEffect(() => {
    const fetchAvailableOffers = async () => {
      await dispatch(getAvailableOffers({ type: "provisioning" })).catch(
        (error) => {
          notyf.open({
            type: "danger",
            message: error,
            dismissible: true,
            ripple: true,
            duration: 5000,
            position: {
              x: "center",
              y: "top",
            },
          });
          handleToggleModal();
        }
      );
    };

    if (activeUser.id && openActivateModal) {
      fetchAvailableOffers();
    }
  }, [
    dispatch,
    activeUser,
    selectedSims,
    openActivateModal,
    notyf,
    handleToggleModal,
  ]);

  if (isDesktop) {
    return (
      <Modal
        show={openActivateModal}
        onHide={() => handleToggleModal()}
        centered
        size="lg"
        backdrop="static"
        scrollable
      >
        <Modal.Header>
          <Modal.Title>Activate SIMs</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validation}
            context={{ requireFile: selectedSims.length === 0 }}
            onSubmit={async (values, { setStatus, setValues }) => {
              const data = {
                imsiList: values.sims.map((sim) => sim.imsi),
                offer: values.offer,
                ...(values.addCustomFieldValues
                  ? {
                      customFieldValues: values.customFieldValues.map((v) => ({
                        customFieldId: v.customFieldId,
                        value: v.value,
                      })),
                    }
                  : {}),
              };
              await dispatch(
                activateSims({
                  sims: data.imsiList,
                  offer: data.offer,
                  customFieldValues: data.customFieldValues,
                })
              )
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "SIMs activated successfully",
                    duration: 5000,
                    dismissible: false,
                    ripple: true,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Success");
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    dismissible: false,
                    ripple: true,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Error");
                })
                .finally(() => {
                  setCurrentStep(3);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              validateForm,
              setFieldTouched,
              status,
              setValues,
              setFieldValue,
            }) => {
              const nextStep = () => {
                validateForm().then((errors) => {
                  if (Object.keys(errors).length === 0) {
                    if (currentStep === 1) {
                      setCurrentStep(2);
                      handleSubmit();
                    }
                  } else {
                    Object.keys(values).forEach((field) => {
                      setFieldTouched(field);
                    });
                  }
                });
              };
              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && values.sims.length === 0 && (
                    <React.Fragment>
                      <Form.Group className="mb-3">
                        <Form.Label>Upload File</Form.Label>

                        <div className="custom-file-upload-wrapper">
                          <label
                            htmlFor="file-upload"
                            className="custom-file-upload-btn"
                            style={{
                              "--dynamic-font-color":
                                whitelabel?.fontColor ?? "#fff",
                              "--dynamic-bg-color":
                                whitelabel?.backgroundColor ?? "#0E1A2B",
                              border: "none",
                            }}
                          >
                            Choose File
                          </label>
                          <input
                            key={fileInputKey}
                            id="file-upload"
                            type="file"
                            name="file"
                            accept=".csv,.xlsx"
                            style={{ display: "none" }}
                            onChange={async (event) => {
                              setFieldValue(
                                "file",
                                event.currentTarget.files[0]
                              );
                              await validateSimsForAction({
                                file: event.currentTarget.files[0],
                                action: "activate",
                              })
                                .then((response) => {
                                  notyf.open({
                                    type: "success",
                                    message: "Sim file validated successfully",
                                    duration: 5000,
                                    ripple: true,
                                    dismissible: false,
                                    position: {
                                      x: "center",
                                      y: "top",
                                    },
                                  });
                                  setFieldValue("sims", response);
                                })
                                .catch((error) => {
                                  notyf.open({
                                    type: "error",
                                    message: error,
                                    duration: 5000,
                                    ripple: true,
                                    dismissible: false,
                                    position: {
                                      x: "center",
                                      y: "top",
                                    },
                                  });
                                  setFieldValue("file", null);
                                  setFileInputKey(new Date());
                                });
                            }}
                            onBlur={() => setFieldTouched("file", true)}
                          />
                          {values.file && (
                            <div className="mt-2">
                              <small className="text-muted">
                                Selected: {values.file.name}
                              </small>
                            </div>
                          )}
                          {touched.file && errors.file && (
                            <div className="text-danger mt-1">
                              {errors.file}
                            </div>
                          )}
                        </div>
                      </Form.Group>
                    </React.Fragment>
                  )}
                  {currentStep === 1 && values.sims.length > 0 && (
                    <React.Fragment>
                      <Form.Group className="mb-3">
                        <Table responsive hover>
                          <thead>
                            <th>ICCID</th>
                            <th>IMSI</th>
                          </thead>
                          <tbody>
                            {values.sims.map((sim) => (
                              <tr key={sim.id}>
                                <td>{sim.iccid}</td>
                                <td>{sim.imsi}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Offer</Form.Label>
                        <Form.Select
                          name="offer"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.offer}
                          isInvalid={Boolean(errors.offer && touched.offer)}
                        >
                          <option value="">Select offer</option>
                          {availableOffers.map((offer) => (
                            <option key={offer.id} value={offer.name}>
                              {offer.name}
                            </option>
                          ))}
                        </Form.Select>
                        {!!touched.offer && (
                          <Form.Control.Feedback type="invalid">
                            {errors.offer}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                      {/* Toggle: Add Custom Field Values */}
                      {customFields.length > 0 && (
                        <Form.Group className="mb-2">
                          <Form.Check
                            type="switch"
                            id="add-cf-values"
                            name="addCustomFieldValues"
                            label="Also set Custom Field Values"
                            checked={values.addCustomFieldValues}
                            onChange={(e) =>
                              setFieldValue(
                                "addCustomFieldValues",
                                e.target.checked
                              )
                            }
                            style={{
                              "--dynamic-bg-color":
                                whitelabel.backgroundColor ?? "#0E1A2B",
                              "--dynamic-font-color":
                                whitelabel.fontColor ?? "#ffffff",
                            }}
                          />
                        </Form.Group>
                      )}
                      {/* Collapse with custom field inputs */}
                      <Collapse
                        in={values.addCustomFieldValues}
                        dimension="height"
                      >
                        <div>
                          {(!customFields || customFields.length === 0) && (
                            <small className="text-muted">
                              No custom fields available.
                            </small>
                          )}
                          {values.customFieldValues.map((cf, idx) => (
                            <Form.Group className="mb-2" key={cf.customFieldId}>
                              <Form.Label>{cf.displayName}</Form.Label>
                              <Form.Control
                                type="text"
                                name={`customFieldValues[${idx}].value`}
                                value={values.customFieldValues[idx].value}
                                onChange={(e) =>
                                  setFieldValue(
                                    `customFieldValues[${idx}].value`,
                                    e.target.value
                                  )
                                }
                                placeholder={`Enter ${cf.displayName}`}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      </Collapse>
                    </React.Fragment>
                  )}
                  {currentStep === 2 && <Loader />}
                  {currentStep === 3 && status === "Success" && (
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <FaCheckCircle size={120} color="#4BBF73" />
                      <h3>Success</h3>
                    </div>
                  )}
                  {currentStep === 3 && status === "Error" && (
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <FaExclamationCircle size={120} color="#FF0000" />
                      <h3 className="mt-3">Order Error</h3>
                      <p>Please contact the administrator.</p>
                    </div>
                  )}
                  <Modal.Footer>
                    {currentStep === 1 && (
                      <React.Fragment>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                          }}
                          onClick={() => {
                            setValues(initialValues);
                            handleToggleModal();
                            if (clearSelectedRows) {
                              clearSelectedRows();
                            }
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          onClick={nextStep}
                          variant="primary"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                          }}
                        >
                          Submit
                        </Button>
                      </React.Fragment>
                    )}
                    {currentStep === 3 && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          setCurrentStep(1);
                          setValues(initialValues);
                          handleToggleModal();
                          if (clearSelectedRows) {
                            clearSelectedRows();
                          }
                        }}
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#ffffff",
                          border: "none",
                        }}
                      >
                        Close
                      </Button>
                    )}
                  </Modal.Footer>
                </Form>
              );
            }}
          </Formik>
        </Modal.Body>
      </Modal>
    );
  } else {
    return (
      <Offcanvas
        show={internalShow}
        placement="bottom"
        scroll
        onHide={handleHide}
        className={`fullscreen-offcanvas ${fadeOut ? "fade-out" : ""}`}
      >
        <Offcanvas.Header closeButton className="fullscreen-offcanvas-header">
          <Offcanvas.Title>Activate SIMs</Offcanvas.Title>
        </Offcanvas.Header>
        <Offcanvas.Body>
          <Formik
            enableReinitialize
            initialValues={initialValues}
            validationSchema={validation}
            onSubmit={async (values, { setStatus, setValues }) => {
              const data = {
                imsiList: values.sims.map((sim) => sim.imsi),
                offer: values.offer,
                ...(values.addCustomFieldValues
                  ? {
                      customFieldValues: values.customFieldValues.map((v) => ({
                        customFieldId: v.customFieldId,
                        value: v.value,
                      })),
                    }
                  : {}),
              };
              await dispatch(
                activateSims({
                  sims: data.imsiList,
                  offer: data.offer,
                  customFieldValues: data.customFieldValues,
                })
              )
                .then(() => {
                  notyf.open({
                    type: "success",
                    message: "SIMs activated successfully",
                    duration: 5000,
                    dismissible: false,
                    ripple: true,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Success");
                })
                .catch((error) => {
                  notyf.open({
                    type: "danger",
                    message: error,
                    duration: 5000,
                    dismissible: false,
                    ripple: true,
                    position: {
                      x: "center",
                      y: "top",
                    },
                  });
                  setStatus("Error");
                })
                .finally(() => {
                  setCurrentStep(3);
                  setValues(initialValues);
                });
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              handleSubmit,
              validateForm,
              setFieldTouched,
              setFieldValue,
              status,
              setValues,
            }) => {
              const nextStep = () => {
                validateForm().then((errors) => {
                  if (Object.keys(errors).length === 0) {
                    if (currentStep === 1) {
                      setCurrentStep(2);
                      handleSubmit();
                    }
                  } else {
                    Object.keys(values).forEach((field) => {
                      setFieldTouched(field);
                    });
                  }
                });
              };
              return (
                <Form onSubmit={handleSubmit}>
                  {currentStep === 1 && (
                    <React.Fragment>
                      <Form.Group className="mb-3">
                        <Table responsive hover>
                          <thead>
                            <th>ICCID</th>
                            <th>IMSI</th>
                          </thead>
                          <tbody>
                            {values.sims.map((sim) => (
                              <tr key={sim.id}>
                                <td>{sim.iccid}</td>
                                <td>{sim.imsi}</td>
                              </tr>
                            ))}
                          </tbody>
                        </Table>
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <Form.Label>Offer</Form.Label>
                        <Form.Select
                          name="offer"
                          onChange={handleChange}
                          onBlur={handleBlur}
                          value={values.offer}
                          isInvalid={Boolean(errors.offer && touched.offer)}
                        >
                          <option value="">Select offer</option>
                          {availableOffers.map((offer) => (
                            <option key={offer.id} value={offer.name}>
                              {offer.name}
                            </option>
                          ))}
                        </Form.Select>
                        {!!touched.offer && (
                          <Form.Control.Feedback type="invalid">
                            {errors.offer}
                          </Form.Control.Feedback>
                        )}
                      </Form.Group>
                      {customFields.length > 0 && (
                        <Form.Group className="mb-2">
                          <Form.Label>Also set Custom Field Values</Form.Label>
                          <Form.Check
                            type="switch"
                            id="add-cf-values-mobile"
                            name="addCustomFieldValues"
                            checked={values.addCustomFieldValues}
                            onChange={(e) =>
                              setFieldValue(
                                "addCustomFieldValues",
                                e.target.checked
                              )
                            }
                            style={{
                              "--dynamic-bg-color":
                                whitelabel.backgroundColor ?? "#0E1A2B",
                              "--dynamic-font-color":
                                whitelabel.fontColor ?? "#ffffff",
                            }}
                          />
                        </Form.Group>
                      )}
                      <Collapse in={values.addCustomFieldValues}>
                        <div>
                          {(!customFields || customFields.length === 0) && (
                            <small className="text-muted">
                              No custom fields available.
                            </small>
                          )}
                          {values.customFieldValues.map((cf, idx) => (
                            <Form.Group className="mb-2" key={cf.customFieldId}>
                              <Form.Label>{cf.displayName}</Form.Label>
                              <Form.Control
                                type="text"
                                name={`customFieldValues[${idx}].value`}
                                value={values.customFieldValues[idx].value}
                                onChange={(e) =>
                                  setFieldValue(
                                    `customFieldValues[${idx}].value`,
                                    e.target.value
                                  )
                                }
                                placeholder={`Enter ${cf.displayName}`}
                              />
                            </Form.Group>
                          ))}
                        </div>
                      </Collapse>
                    </React.Fragment>
                  )}
                  {currentStep === 2 && <Loader />}
                  {currentStep === 3 && status === "Success" && (
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <FaCheckCircle size={80} color="#4BBF73" />
                      <h3>Success</h3>
                    </div>
                  )}
                  {currentStep === 3 && status === "Error" && (
                    <div className="d-flex flex-column align-items-center justify-content-center">
                      <FaExclamationCircle size={80} color="#FF0000" />
                      <h3 className="mt-3">Order Error</h3>
                      <p>Please contact the administrator.</p>
                    </div>
                  )}
                  <div className="d-flex flex-column align-items-center mt-3">
                    {currentStep === 1 && (
                      <React.Fragment>
                        <Button
                          onClick={nextStep}
                          variant="primary"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                          className="mb-2"
                        >
                          Submit
                        </Button>
                        <Button
                          variant="primary"
                          style={{
                            "--dynamic-bg-color":
                              whitelabel?.backgroundColor ?? "#0E1A2B",
                            "--dynamic-font-color":
                              whitelabel?.fontColor ?? "#ffffff",
                            border: "none",
                            padding: "10px",
                            width: "100%",
                          }}
                          onClick={() => {
                            setValues(initialValues);
                            handleHide();
                            if (clearSelectedRows) {
                              clearSelectedRows();
                            }
                          }}
                        >
                          Cancel
                        </Button>
                      </React.Fragment>
                    )}
                    {currentStep === 3 && (
                      <Button
                        variant="primary"
                        onClick={() => {
                          setCurrentStep(1);
                          setValues(initialValues);
                          handleHide();
                          if (clearSelectedRows) {
                            clearSelectedRows();
                          }
                        }}
                        style={{
                          "--dynamic-bg-color":
                            whitelabel?.backgroundColor ?? "#0E1A2B",
                          "--dynamic-font-color":
                            whitelabel?.fontColor ?? "#ffffff",
                          border: "none",
                          width: "100%",
                          padding: "10px",
                        }}
                      >
                        Close
                      </Button>
                    )}
                  </div>
                </Form>
              );
            }}
          </Formik>
        </Offcanvas.Body>
      </Offcanvas>
    );
  }
};

export default ActivateSimModal;
